"use client"

import { useEffect, useMemo, useState } from "react"
import {
  format,
  addDays,
  addMonths,
  subMonths,
  startOfWeek,
  startOfMonth,
  startOfDay,
  eachDayOfInterval,
  isSameDay,
  isSameMonth,
  isBefore,
} from "date-fns"
import { ChevronLeft, ChevronRight, CalendarDays, AlignLeft, Loader2 } from "lucide-react"
import { cn } from "@/lib/utils"

// Minimal/light booking picker. Reads REAL availability from the slot engine
// (/api/appointments/availability) — the barber's schedule minus existing
// appointments minus buffer. Linear + Calendar modes share one selection.

type Period = "Morning" | "Afternoon" | "Evening"
interface Slot {
  time: string
  label: string
  period: Period
  available: boolean
}

const PERIODS: Period[] = ["Morning", "Afternoon", "Evening"]
const WEEKDAYS = ["Mo", "Tu", "We", "Th", "Fr", "Sa", "Su"]

export function BookingTimePicker({
  barberId,
  durationMin = 30,
  onSelect,
}: {
  barberId: string
  durationMin?: number
  onSelect?: (iso: string) => void
}) {
  const today = useMemo(() => startOfDay(new Date()), [])
  // Default to the List ("linear") view — it reads calmer and doesn't jump the
  // layout the way the month grid does when it opens. Clients can switch to
  // Calendar via the toggle.
  const [mode, setMode] = useState<"linear" | "calendar">("linear")
  const [selectedDate, setSelectedDate] = useState<Date>(today)
  const [selectedSlot, setSelectedSlot] = useState<string | null>(null)
  const [month, setMonth] = useState<Date>(startOfMonth(today))
  const [weekStart, setWeekStart] = useState<Date>(today)

  const [slots, setSlots] = useState<Slot[]>([])
  const [loading, setLoading] = useState(false)
  const [working, setWorking] = useState(true)

  // Fetch real availability whenever the chosen date (or service duration) changes.
  useEffect(() => {
    let cancelled = false
    setLoading(true)
    setSelectedSlot(null)
    const dateStr = format(selectedDate, "yyyy-MM-dd")
    // Viewer's timezone so the server resolves the barber's wall-clock hours to
    // real instants in it (past/conflict checks) — matches pickSlot's local setHours.
    const tz = Intl.DateTimeFormat().resolvedOptions().timeZone
    fetch(`/api/appointments/availability?barber=${barberId}&date=${dateStr}&duration=${durationMin}&tz=${encodeURIComponent(tz)}`)
      .then((r) => r.json())
      .then((json) => {
        if (cancelled) return
        setSlots(json.slots || [])
        setWorking(json.working !== false)
      })
      .catch(() => {
        if (!cancelled) setSlots([])
      })
      .finally(() => {
        if (!cancelled) setLoading(false)
      })
    return () => { cancelled = true }
  }, [selectedDate, barberId, durationMin])

  // On load, jump to the barber's first open day so the picker doesn't land on
  // an empty "today" (e.g. at night or on a closed day). Falls back to today.
  useEffect(() => {
    let cancelled = false
    fetch(`/api/appointments/next-available?barber=${barberId}&duration=${durationMin}&tz=${encodeURIComponent(Intl.DateTimeFormat().resolvedOptions().timeZone)}`)
      .then((r) => r.json())
      .then((json) => {
        if (cancelled || !json?.next?.date) return
        const d = startOfDay(new Date(`${json.next.date}T00:00:00`))
        setSelectedDate(d)
        setMonth(startOfMonth(d))
        setWeekStart(d)
      })
      .catch(() => {})
    return () => { cancelled = true }
  }, [barberId, durationMin])

  function pickDate(d: Date) {
    setSelectedDate(d)
  }
  function pickSlot(s: Slot) {
    setSelectedSlot(s.time)
    const [h, m] = s.time.split(":").map(Number)
    const d = new Date(selectedDate)
    d.setHours(h, m, 0, 0)
    onSelect?.(d.toISOString())
  }

  const weekDays = useMemo(
    () => eachDayOfInterval({ start: weekStart, end: addDays(weekStart, 6) }),
    [weekStart],
  )
  const monthDays = useMemo(() => {
    const gridStart = startOfWeek(startOfMonth(month), { weekStartsOn: 1 })
    return eachDayOfInterval({ start: gridStart, end: addDays(gridStart, 41) })
  }, [month])

  const SlotButton = ({ s }: { s: Slot }) => (
    <button
      type="button"
      disabled={!s.available}
      onClick={() => pickSlot(s)}
      className={cn(
        "h-9 px-3 rounded-lg font-mono text-xs tracking-tight transition-all",
        !s.available && "text-muted-foreground/40 cursor-not-allowed line-through",
        s.available && selectedSlot === s.time && "bg-foreground text-background",
        s.available && selectedSlot !== s.time && "border border-border/70 hover:border-foreground/40",
      )}
    >
      {s.label}
    </button>
  )

  const Arrow = ({ dir, onClick }: { dir: "l" | "r"; onClick: () => void }) => (
    <button type="button" onClick={onClick} className="p-1 text-muted-foreground/60 hover:text-foreground transition-colors">
      {dir === "l" ? <ChevronLeft className="w-4 h-4" /> : <ChevronRight className="w-4 h-4" />}
    </button>
  )

  const SlotsArea = ({ layout }: { layout: "grouped" | "grid" }) => {
    if (loading) {
      return <div className="py-8 flex justify-center"><Loader2 className="w-4 h-4 animate-spin text-muted-foreground" /></div>
    }
    if (!working) {
      return <p className="py-6 text-center text-xs text-muted-foreground">Not working this day.</p>
    }
    if (slots.length === 0) {
      return <p className="py-6 text-center text-xs text-muted-foreground">No times available.</p>
    }
    if (layout === "grid") {
      return (
        <div className="grid grid-cols-3 gap-2">
          {slots.map((s) => <SlotButton key={s.time} s={s} />)}
        </div>
      )
    }
    return (
      <div className="space-y-4">
        {PERIODS.map((p) => {
          const group = slots.filter((s) => s.period === p)
          if (group.length === 0) return null
          return (
            <div key={p}>
              <p className="text-[11px] uppercase tracking-wide text-muted-foreground/70 mb-2">{p}</p>
              <div className="flex flex-wrap gap-2">{group.map((s) => <SlotButton key={s.time} s={s} />)}</div>
            </div>
          )
        })}
      </div>
    )
  }

  return (
    <div className="rounded-[2rem] bg-card bk-glass shadow-sm p-5">
      <div className="flex items-center justify-between mb-4">
        <p className="text-sm font-medium text-muted-foreground">Select a date &amp; time</p>
        <div className="inline-flex items-center rounded-full bg-muted p-0.5">
          {([["linear", "List", AlignLeft], ["calendar", "Calendar", CalendarDays]] as const).map(([m, label, Icon]) => (
            <button
              key={m}
              type="button"
              onClick={() => setMode(m)}
              className={cn(
                "inline-flex items-center gap-1 h-7 px-3 rounded-full text-xs font-medium transition-colors",
                mode === m ? "bg-background shadow-sm text-foreground" : "text-muted-foreground",
              )}
            >
              <Icon className="w-3.5 h-3.5" /> {label}
            </button>
          ))}
        </div>
      </div>

      {mode === "linear" ? (
        <>
          <div className="flex items-center justify-between mb-3">
            <div className="flex items-baseline gap-1.5">
              <span className="text-base font-medium">{format(weekStart, "MMMM")}</span>
              <span className="font-mono text-sm text-muted-foreground/50">{format(weekStart, "yy")}</span>
            </div>
            <div className="flex items-center">
              <Arrow dir="l" onClick={() => setWeekStart(addDays(weekStart, -7))} />
              <Arrow dir="r" onClick={() => setWeekStart(addDays(weekStart, 7))} />
            </div>
          </div>
          <div className="grid grid-cols-7 gap-1">
            {weekDays.map((d) => {
              const past = isBefore(d, today)
              const active = isSameDay(d, selectedDate)
              return (
                <button
                  key={d.toISOString()}
                  type="button"
                  disabled={past}
                  onClick={() => pickDate(d)}
                  className={cn(
                    "flex flex-col items-center py-1.5 rounded-xl transition-colors",
                    past && "text-muted-foreground/30 cursor-not-allowed",
                    !past && active && "bg-foreground text-background",
                    !past && !active && "hover:bg-muted",
                  )}
                >
                  <span className="text-[10px] uppercase tracking-wide opacity-60">{format(d, "EEEEEE")}</span>
                  <span className="font-mono text-sm">{format(d, "d")}</span>
                </button>
              )
            })}
          </div>
          <div className="mt-5"><SlotsArea layout="grouped" /></div>
        </>
      ) : (
        <>
          <div className="flex items-center justify-between mb-2">
            <div className="flex items-baseline gap-1.5">
              <span className="text-base font-medium">{format(month, "MMMM")}</span>
              <span className="font-mono text-sm text-muted-foreground/50">{format(month, "yy")}</span>
            </div>
            <div className="flex items-center">
              <Arrow dir="l" onClick={() => setMonth(subMonths(month, 1))} />
              <Arrow dir="r" onClick={() => setMonth(addMonths(month, 1))} />
            </div>
          </div>
          <div className="grid grid-cols-7 mb-1">
            {WEEKDAYS.map((d) => (
              <span key={d} className="text-center font-mono text-[10px] text-muted-foreground/50">{d}</span>
            ))}
          </div>
          <div className="grid grid-cols-7 gap-0.5">
            {monthDays.map((d) => {
              const past = isBefore(d, today)
              const active = isSameDay(d, selectedDate)
              const dim = !isSameMonth(d, month)
              const isToday = isSameDay(d, today)
              return (
                <button
                  key={d.toISOString()}
                  type="button"
                  disabled={past}
                  onClick={() => pickDate(d)}
                  className={cn(
                    "h-8 grid place-items-center font-mono text-sm rounded-lg transition-colors",
                    past && "text-muted-foreground/25 cursor-not-allowed",
                    !past && active && "bg-foreground text-background",
                    !past && !active && "hover:bg-muted",
                    dim && !active && "text-muted-foreground/30",
                    isToday && !active && "underline underline-offset-4 decoration-muted-foreground/40",
                  )}
                >
                  {format(d, "d")}
                </button>
              )
            })}
          </div>
          <div className="mt-4">
            <p className="text-[11px] uppercase tracking-wide text-muted-foreground/70 mb-2">{format(selectedDate, "EEE, MMM d")}</p>
            <SlotsArea layout="grid" />
          </div>
        </>
      )}
    </div>
  )
}
